import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import { trackerKeys } from './hooks';
import { addDaysIso } from './leadFollowups';
import { todayIso } from './trackerFormat';

/**
 * Follow-up rounds, as the drawer reads and writes them.
 *
 * ── What a round is, in Kyu's words ────────────────────────────────────────
 *
 * A "cycle" means a ROUND of follow-ups, and the seven business days is only
 * round one. If a round ends with no answer the lead is left alone and parked,
 * with the next round scheduled for a date somebody picks. It repeats, with no
 * limit. Every round is set by hand BUT SHAPED — a start date, a number of
 * follow-ups, and a window to do them in — which is what makes round four as
 * measurable as round one.
 *
 * The behaviour lives in the database (20261214000100): a touch joins the
 * running round by trigger, and the round ends on whichever comes first of the
 * count being met, the window closing, or a reply. Nothing here decides any of
 * that. This module starts rounds, reads them back, and does the arithmetic
 * the screen needs.
 */

export type WindowUnit = 'business_days' | 'weeks';

export interface RoundShape {
  touchTarget: number;
  windowLength: number;
  windowUnit: WindowUnit;
}

/**
 * Round one, which is a PRESET of the same shape and not a special case.
 *
 * Kyu: for the first 7 business days, reach out every business day until they
 * respond. It is deliberately the heaviest round; later ones are picked by
 * hand and usually looser.
 */
export const ROUND_ONE: RoundShape = {
  touchTarget: 7,
  windowLength: 7,
  windowUnit: 'business_days',
};

/** The ceiling the database enforces on touch_target. */
export const MAX_TOUCH_TARGET = 10;

/*
 * PACE_GRACE was here, and it is gone.
 *
 * It was the number of missed days a round could run light before being
 * called Behind — set to one, by me, never ratified by Kyu, and flagged in
 * this file as an unratified decision for three drops. He answered it on
 * 29 Aug by rejecting the whole frame rather than the number: a lead at 3 of 7
 * is not failing, it is unfinished. There is no threshold now because there is
 * no judgement to tune. See roundProgress.
 */

export const WINDOW_UNIT_OPTIONS: { value: WindowUnit; label: string }[] = [
  { value: 'business_days', label: 'business days' },
  { value: 'weeks', label: 'weeks' },
];

export const ENDED_REASON_LABELS: Record<string, string> = {
  count_met: 'All follow-ups sent',
  window_closed: 'Window closed, no answer',
  replied: 'They replied',
};

export function isWindowUnit(value: string): value is WindowUnit {
  return value === 'business_days' || value === 'weeks';
}

export function windowLabel(length: number, unit: WindowUnit): string {
  const noun = unit === 'weeks' ? 'week' : 'business day';
  return `${String(length)} ${noun}${length === 1 ? '' : 's'}`;
}

export function endedReasonLabel(reason: string | null): string {
  if (reason === null) {
    return 'Running';
  }
  return ENDED_REASON_LABELS[reason] ?? reason;
}

// ── The arithmetic ─────────────────────────────────────────────────────────

function isWeekend(iso: string): boolean {
  const day = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return day === 0 || day === 6;
}

function diffDays(fromIso: string, toIso: string): number {
  return Math.round(
    (Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86400000,
  );
}

/** A window can never need more calendar days than this to be walked. */
function walkLimit(length: number): number {
  return length * 2 + 14;
}

/**
 * The last day a round may be worked, inclusive.
 *
 * ⚠ THIS MUST AGREE WITH `public.round_window_end` IN SQL. Two implementations
 * of one rule is a real cost, and it is paid deliberately: the database owns
 * the answer for rounds that exist, and the builder has to show "this would
 * end on the 17th" for a round that does not exist yet.
 *
 * They are kept in step by testing BOTH against the same fixed dates — August
 * 2026, which begins on a Saturday — in this module's tests and in
 * scripts/verify-migrations.sh. A change to one that is not made to the other
 * fails on the same six assertions.
 *
 * WEEKS are calendar weeks inclusive of the start day: 1 week from Monday the
 * 3rd ends Sunday the 9th. The mockup added the length in DAYS here and ended
 * every round after the first five times too early.
 *
 * BUSINESS DAYS count Monday to Friday from the first working day on or after
 * the start, so a round begun on a Saturday begins counting on the Monday and
 * weekends are never misses.
 */
export function windowEndOf(startsOn: string, length: number, unit: WindowUnit): string {
  if (length < 1) {
    return startsOn;
  }
  if (unit === 'weeks') {
    return addDaysIso(startsOn, length * 7 - 1);
  }
  let day = startsOn;
  let counted = 0;
  for (let step = 0; step < walkLimit(length); step += 1) {
    if (!isWeekend(day)) {
      counted += 1;
      if (counted === length) {
        return day;
      }
    }
    day = addDaysIso(day, 1);
  }
  return day;
}

/** How long the window is, counted the way that window counts. */
export function windowLengthInDays(shape: RoundShape): number {
  return shape.windowUnit === 'weeks' ? shape.windowLength * 7 : shape.windowLength;
}

/**
 * How much of the window has been used by `today`, counted its own way.
 *
 * Business-day windows count working days, so a round that started Friday is
 * one day in on Monday and not three. Clamped at both ends: nothing before the
 * start counts, and nothing past the window end counts, so a round nobody
 * closed for a month does not report a pace of 400%.
 */
export function daysElapsed(
  startsOn: string,
  today: string,
  unit: WindowUnit,
  windowEnd: string,
): number {
  const last = today < windowEnd ? today : windowEnd;
  if (last < startsOn) {
    return 0;
  }
  if (unit === 'weeks') {
    return diffDays(startsOn, last) + 1;
  }
  let counted = 0;
  let day = startsOn;
  // Bounded by the window itself, which is bounded by walkLimit above.
  for (let step = 0; step <= walkLimit(diffDays(startsOn, last) + 1); step += 1) {
    if (day > last) {
      break;
    }
    if (!isWeekend(day)) {
      counted += 1;
    }
    day = addDaysIso(day, 1);
  }
  return counted;
}

export type RoundPace = 'not-started' | 'on-track' | 'running-out';

export interface RoundProgress {
  touchesDone: number;
  touchTarget: number;
  windowEnd: string;
  /** Follow-ups still owed. Never below zero. */
  touchesLeft: number;
  /** Window days still to run, counted the window's own way. Zero on the last day. */
  daysLeft: number;
  /**
   * How long the whole window is, in the same days `daysLeft` counts.
   *
   * Exposed because the bar needs it and the alternative was worse: it was
   * briefly drawn using `touchTarget` as the window length, which is a
   * different number the moment a round is not 7-over-7 — a 3-over-2-weeks
   * round would have drawn its pace mark a third of the way along on day one.
   */
  windowDays: number;
  /**
   * Days this round can still send on, TODAY INCLUDED.
   *
   * `daysLeft` counts the days *after* today, so it reads 6 on the first day
   * of a seven-day window. Comparing follow-ups against it directly said a
   * fresh 7-over-7 round was already impossible before anybody had done
   * anything, which is the off-by-one this field exists to remove. Every pace
   * question is asked against this one, never against daysLeft.
   */
  daysToSendIn: number;
  pace: RoundPace;
}

/**
 * Where a running round stands today: two counts and one comparison.
 *
 * ── What this used to be, and why it changed ───────────────────────────────
 *
 * It used to spread the target evenly across the window, call the result
 * `expected`, subtract what was done, and declare the lead BEHIND when the
 * gap exceeded a grace of one.
 *
 * Kyu killed it, 29 Aug: "3/7 behind — well are we really behind, or are we
 * just at the 3rd email yet? Red psychologically implies negative. We're
 * trying to measure progress relative to how far we are in finishing the
 * round."
 *
 * He was right, and the arithmetic proves it. A round is capped at one touch
 * a day, so a 7-over-7 round has to send every single day — miss one and you
 * are permanently "behind" with no spare day to recover in. The label named a
 * fault you could not fix, and the grace threshold that softened it was a
 * number nobody had ever ratified.
 *
 * So there is no expectation curve any more, and nothing to tune. Two facts:
 * how many follow-ups are left, and how many days are left. The one judgement
 * that survives is not a judgement at all but an impossibility — you cannot
 * send more follow-ups than you have days to send them in.
 */
export function roundProgress(input: {
  startsOn: string;
  touchTarget: number;
  windowLength: number;
  windowUnit: WindowUnit;
  touchesDone: number;
  windowEnd: string;
  today: string;
}): RoundProgress {
  const total = windowLengthInDays({
    touchTarget: input.touchTarget,
    windowLength: input.windowLength,
    windowUnit: input.windowUnit,
  });
  const used = Math.min(
    daysElapsed(input.startsOn, input.today, input.windowUnit, input.windowEnd),
    total,
  );
  const touchesLeft = Math.max(0, input.touchTarget - input.touchesDone);
  const daysLeft = Math.max(0, total - used);
  // Today counts. See daysToSendIn.
  const daysToSendIn = daysLeft + 1;
  // The only alarm left, and it is arithmetic rather than opinion: one touch a
  // day is the ceiling (lead_rounds_pace, 20261213000100), so more follow-ups
  // owed than days remaining means this round cannot be completed — whoever
  // reads it can act today or accept a short round, and both are real choices.
  const pace: RoundPace =
    input.today < input.startsOn
      ? 'not-started'
      : touchesLeft > daysToSendIn
        ? 'running-out'
        : 'on-track';
  return {
    touchesDone: input.touchesDone,
    touchTarget: input.touchTarget,
    windowEnd: input.windowEnd,
    touchesLeft,
    daysLeft,
    windowDays: total,
    daysToSendIn,
    pace,
  };
}

/**
 * The most follow-ups a window of this length may be asked for.
 *
 * One a day is the ceiling — Kyu: "Who's gonna do follow ups more than once a
 * day? That's Oracle." The database refuses a faster shape outright, so the
 * picker offers only shapes that can be created rather than letting somebody
 * choose one and then be told no.
 */
export function maxTouchesFor(length: number, unit: WindowUnit): number {
  const days = unit === 'weeks' ? length * 7 : length;
  return Math.max(1, Math.min(MAX_TOUCH_TARGET, days));
}

export function paceIsPossible(shape: RoundShape): boolean {
  return shape.touchTarget >= 1 && shape.touchTarget <= windowLengthInDays(shape);
}

// ── Reading rounds ─────────────────────────────────────────────────────────

export const roundKeys = {
  running: () => [...trackerKeys.all, 'rounds', 'running'] as const,
  forLead: (leadId: string) => [...trackerKeys.all, 'rounds', 'lead', leadId] as const,
  sweep: (today: string) => [...trackerKeys.all, 'rounds', 'sweep', today] as const,
};

/** One lead's running round, as `lead_round_state` reports it. */
export interface RunningRound {
  leadId: string;
  roundId: string;
  number: number;
  startsOn: string;
  touchTarget: number;
  windowLength: number;
  windowUnit: WindowUnit;
  windowEnd: string;
  touchesDone: number;
}

/** A round in a lead's history, running or ended. */
export interface LeadRound {
  id: string;
  number: number;
  starts_on: string;
  touch_target: number;
  window_length: number;
  window_unit: string;
  ended_on: string | null;
  ended_reason: string | null;
}

const ROUND_COLUMNS =
  'id, number, starts_on, touch_target, window_length, window_unit, ended_on, ended_reason';

/**
 * Every running round, keyed by lead.
 *
 * The whole view rather than a query per lead, for the reason the follow-up
 * column had to learn the hard way: `.in('lead_id', ids)` with 787 leads is a
 * 31,000-character URL. This one is bounded by how many leads are being worked
 * right now, which is a much smaller number than how many exist.
 *
 * Every column of a view is nullable to the type generator, so a row missing
 * anything it needs is DROPPED rather than filled in with a guess — the same
 * refusal stageKeys.ts makes.
 */
export async function fetchRunningRounds(
  client: SupabaseClient,
): Promise<Map<string, RunningRound>> {
  const { data, error } = await client.from('lead_round_state').select('*');
  if (error) {
    throw new Error(error.message);
  }
  const byLead = new Map<string, RunningRound>();
  for (const row of data ?? []) {
    if (
      row.lead_id === null ||
      row.round_id === null ||
      row.number === null ||
      row.starts_on === null ||
      row.touch_target === null ||
      row.window_length === null ||
      row.window_end === null ||
      row.window_unit === null ||
      !isWindowUnit(row.window_unit)
    ) {
      continue;
    }
    byLead.set(row.lead_id, {
      leadId: row.lead_id,
      roundId: row.round_id,
      number: row.number,
      startsOn: row.starts_on,
      touchTarget: row.touch_target,
      windowLength: row.window_length,
      windowUnit: row.window_unit,
      windowEnd: row.window_end,
      touchesDone: row.touches_done ?? 0,
    });
  }
  return byLead;
}

export function useRunningRounds() {
  const { client } = useAuth();
  return useQuery({
    queryKey: roundKeys.running(),
    queryFn: () => fetchRunningRounds(client),
    retry: false,
  });
}

/** One lead's rounds, newest first. Fetched when a drawer opens, not before. */
export function useLeadRounds(leadId: string | null) {
  const { client } = useAuth();
  return useQuery({
    queryKey: roundKeys.forLead(leadId ?? ''),
    enabled: leadId !== null,
    retry: false,
    queryFn: async (): Promise<LeadRound[]> => {
      const { data, error } = await client
        .from('lead_rounds')
        .select(ROUND_COLUMNS)
        .eq('lead_id', leadId ?? '')
        .order('number', { ascending: false });
      if (error) {
        throw new Error(error.message);
      }
      return data ?? [];
    },
  });
}

/**
 * The sweep that ends rounds whose window has passed.
 *
 * Nothing in a database notices that a day has gone by, so a round that ran
 * out on Friday is still open on Monday until somebody asks. This asks, once,
 * when the Tracker loads.
 *
 * A query rather than a mutation, deliberately: react-query already
 * de-duplicates a query by key, and the key carries TODAY, so opening three
 * tabs runs it once and opening one tomorrow runs it again. `staleTime:
 * Infinity` keeps a window focus from repeating it. The call is idempotent
 * anyway — it dates each ending from the round rather than from now — so the
 * cost of a repeat would be nothing; this just avoids the noise.
 *
 * A failure is SWALLOWED and reported as zero. Somebody without sales access
 * gets a refusal from the function, and a red banner about a background sweep
 * they did not ask for would be the wrong thing to show them.
 */
export function useRoundSweep(enabled: boolean) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const today = todayIso();
  return useQuery({
    queryKey: roundKeys.sweep(today),
    enabled,
    retry: false,
    staleTime: Infinity,
    queryFn: async (): Promise<number> => {
      const { data, error } = await client.rpc('close_due_rounds', { p_today: today });
      if (error) {
        return 0;
      }
      const closed = typeof data === 'number' ? data : 0;
      if (closed > 0) {
        // Rounds ended and tasks were raised, so both lists are stale.
        void queryClient.invalidateQueries({ queryKey: trackerKeys.all });
        void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
      }
      return closed;
    },
  });
}

// ── Starting one ───────────────────────────────────────────────────────────

export function useStartLeadRound() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      leadId: string;
      startsOn: string;
      touchTarget: number;
      windowLength: number;
      windowUnit: WindowUnit;
    }): Promise<string> => {
      const { data, error } = await client.rpc('start_lead_round', {
        p_lead: input.leadId,
        p_starts_on: input.startsOn,
        p_touch_target: input.touchTarget,
        p_window_length: input.windowLength,
        p_window_unit: input.windowUnit,
      });
      if (error) {
        throw new Error(error.message);
      }
      return typeof data === 'string' ? data : '';
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.all });
      // Starting a round completes the previous one's "schedule the next round
      // or close this lead" task, which lives in somebody's My Tasks.
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/**
 * Start a round for many leads at once.
 *
 * Kyu, 31 Aug: "I just want the team to have that ability to do that."
 *
 * The exclusions are the database's, not this hook's. `planEnrolment` works
 * out what SHOULD happen so the panel can say so before anybody presses
 * anything; `start_rounds_for_leads` decides what DOES happen. A bulk write
 * that trusted the browser's idea of who is eligible is what put eight live
 * deals into a chase sequence on 31 August.
 *
 * So the result comes back per lead — a round, or a reason — and the caller
 * reports what actually landed rather than assuming the plan held.
 */
export interface BulkRoundResult {
  leadId: string;
  roundId: string | null;
  number: number | null;
  skipped: string | null;
}

export function useStartRoundsForLeads() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      leadIds: string[];
      startsOn: string;
      touchTarget: number;
      windowLength: number;
      windowUnit: WindowUnit;
    }): Promise<BulkRoundResult[]> => {
      const { data, error } = await client.rpc('start_rounds_for_leads', {
        p_leads: input.leadIds,
        p_starts_on: input.startsOn,
        p_touch_target: input.touchTarget,
        p_window_length: input.windowLength,
        p_window_unit: input.windowUnit,
      });
      if (error) {
        throw new Error(error.message);
      }
      return (Array.isArray(data) ? data : []).map((row) => ({
        leadId: row.lead_id,
        roundId: row.round_id,
        number: row.number,
        skipped: row.skipped,
      }));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/** What the function's refusals mean, in words somebody can act on. */
export function roundErrorMessage(raw: string): string {
  if (raw.includes('not_a_sales_member')) {
    return 'You need sales access to start a round.';
  }
  if (raw.includes('round_already_running')) {
    return 'This lead is already in a round. That one ends before another can start.';
  }
  if (raw.includes('round_pace_too_fast')) {
    return 'That is more than one follow-up a day. Give it a longer window, or ask for fewer.';
  }
  if (raw.includes('round_window_unit_unknown')) {
    return 'Pick business days or weeks.';
  }
  if (raw.includes('round_needs_a_shape')) {
    return 'A round needs a number of follow-ups and a window to do them in.';
  }
  if (raw.includes('round_needs_a_start')) {
    return 'Pick the day this round starts.';
  }
  if (raw.includes('lead_not_found')) {
    return 'That lead no longer exists.';
  }
  return 'Could not start the round. Try again.';
}
